import React, { useState } from "react";
import styles from "./Home.module.css";
import { reactIcons } from "../../icons/reactIcons";
import noImage from "../../assets/images/no_image.png";
import ReviewImageModal from "./modal/ReviewImageModal";

interface HomeReportsProps {
  reports: any[];
}

const getGeoTag = (geoTag: any) => (Array.isArray(geoTag) ? geoTag[0] : geoTag);

const HomeReports: React.FC<HomeReportsProps> = ({ reports }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [activeImages, setActiveImages] = useState<string[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [imageLoading, setImageLoading] = useState<Record<string, boolean>>({});

  const handleImageClick = (images: string[], index: number) => {
    setActiveImages(images);
    setActiveIndex(index);
    setModalOpen(true);
  };

  const openInMaps = (lat: number, lon: number) => {
    const scheme = /iPhone|iPad|Macintosh/i.test(navigator.userAgent)
      ? `maps://maps.apple.com/?q=${lat},${lon}`
      : `https://www.google.com/maps?q=${lat},${lon}`;

    window.open(scheme, "_blank");
  };

  if (!reports.length) {
    return (
      <div className={styles.noReports}>
        <div className={styles.noReportsCard}>
          <reactIcons.ReportIcon className={styles.noReportsIcon} />
          <h3>No Reports Yet</h3>
          <p>Your submitted reports will appear here once available.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.reportList}>
      {reports.map((report) => {
        const formattedDate = new Date(report.createdAt).toLocaleString();
        const images = report.files?.map((f: any) => f.signedUrl) || [];
        const geoTag = getGeoTag(report.geoTag);
        const reportedConcerns = [
          ["Illegal Smoking", geoTag?.illegalSmoking],
          ["Zoning Concern", geoTag?.zoningConcern],
          ["License Concern", geoTag?.licenseConcern],
        ].filter(([, value]) => value === "Yes");

        return (
          <div key={report.id} className={styles.reportCard}>
            <div className={styles.reportRow}>
              <reactIcons.CalenderIcon className={styles.icon} />
              <div>
                <strong>Date Submitted</strong>
                <p>{formattedDate}</p>
              </div>
            </div>

            <div className={styles.reportRow}>
              <reactIcons.MapPinIcon className={styles.icon} />
              <div>
                <strong>Location</strong>
                <p>
                  {report.address || geoTag?.address || "Address unavailable"}
                </p>
                <p className={styles.lat_lng}>
                  {geoTag?.lat}° N, {geoTag?.lon || geoTag?.long}° W
                </p>

                <button
                  className={styles.visitButton}
                  onClick={() =>
                    openInMaps(
                      Number(geoTag?.lat),
                      Number(geoTag?.lon || geoTag?.long),
                    )
                  }
                >
                  <reactIcons.FaMapMarkerIcon color="#0155ae" /> Visit Location
                </button>
              </div>
            </div>

            <div className={styles.reportRow}>
              <div>
                <reactIcons.NoteIcon className={styles.icon} />
              </div>
              <div>
                <strong>Note</strong>
                <p>{report.notes}</p>
              </div>
            </div>

            <div className={styles.concernsCard}>
              <div className={styles.concernsHeading}>
                <span className={styles.concernsHeadingIcon}>✦</span>
                <div>
                  <strong>Concerns</strong>
                  <span>Report highlights at a glance</span>
                </div>
              </div>

              <div className={styles.concernsGrid}>
                {reportedConcerns.length > 0 ? (
                  reportedConcerns.map(([label]) => (
                    <div className={styles.concernChip} key={label}>
                      {label}
                    </div>
                  ))
                ) : (
                  <span className={styles.noConcerns}>No concerns reported</span>
                )}
              </div>
            </div>

            <div className={styles.imageSection}>
              <strong>Images</strong>
              <div className={styles.imageGrid}>
                {images.length > 0 ? (
                  images.map((img: any, i: number) => {
                    const key = `${report.id}-${i}`;

                    return (
                      <div className={styles.imageWrapper} key={key}>
                        {imageLoading[key] !== false && (
                          <div className={styles.imageSpinner}></div>
                        )}

                        <img
                          src={img}
                          alt={`report-${i}`}
                          className={`${styles.reportImage} ${
                            imageLoading[key] !== false
                              ? styles.hiddenImage
                              : ""
                          }`}
                          onLoad={() =>
                            setImageLoading((prev) => ({
                              ...prev,
                              [key]: false,
                            }))
                          }
                          onError={() =>
                            setImageLoading((prev) => ({
                              ...prev,
                              [key]: false,
                            }))
                          }
                          onClick={() => handleImageClick(images, i)}
                        />
                      </div>
                    );
                  })
                ) : (
                  <img src={noImage} className={styles.reportImage} />
                )}
              </div>
            </div>
          </div>
        );
      })}

      <ReviewImageModal
        open={modalOpen}
        images={activeImages}
        currentIndex={activeIndex}
        onClose={() => setModalOpen(false)}
        onNext={() => setActiveIndex((prev) => prev + 1)}
        onPrev={() => setActiveIndex((prev) => prev - 1)}
      />
    </div>
  );
};

export default HomeReports;
